import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import maplibregl from 'maplibre-gl';
import { map } from '../map/core/MapView';
import { isConditionMatching, isLast30DaysMatching, isInstallationMatching } from './filterUtil'; // Add isInstallationMatching

export default (keyword, filter, showTerminated, filterSort, filterMap, positions, setShowTerminated, setFilteredDevices, setFilteredPositions, distanceUnit, autoZoomOnFilter = false) => {
  const groups = useSelector((state) => state.groups.items);
  const devices = useSelector((state) => state.devices.items);

  // Use a ref to track if we've already applied the filter-triggered zoom
  const hasZoomedRef = useRef(false);

  // Helper function to get device groups
  const getDeviceGroups = (device) => {
    const groupIds = [];
    let { groupId } = device;
    while (groupId) {
      groupIds.push(groupId);
      groupId = groups[groupId]?.groupId || 0;
    }
    return groupIds;
  };

  // Helper function to check if device matches keyword
  const matchesKeyword = (device, searchKeyword) => {
    const upperCaseKeyword = searchKeyword.toUpperCase();
    return [device.name, device.uniqueId, device.phone, device.contact, device.address, device.vin, device.vehicleMake]
      .some((s) => s && s.toUpperCase().includes(upperCaseKeyword));
  };

  // Helper function to check all device filters
  const getFilteredDevices = (allDevices, isTerminated, searchKeyword, filterCriteria, distUnit) => {
    if (!allDevices) return [];

    const filtered = isTerminated
      ? Object.values(allDevices).filter((device) => device.disabled === true)
        .filter((device) => matchesKeyword(device, searchKeyword))
      : Object.values(allDevices)
        .filter((device) => device.disabled === false)
        .filter((device) => !filterCriteria.statuses.length ||
          filterCriteria.statuses.includes(device.status) ||
          (filterCriteria.statuses.includes('terminated') && device.disabled === true))
        .filter((device) => !filterCriteria.groups.length ||
          getDeviceGroups(device).some((id) => filterCriteria.groups.includes(id)))
        .filter((device) => matchesKeyword(device, searchKeyword))
        .filter((device) => {
          if (!filterCriteria.conditions || !filterCriteria.conditions.length) {
            return true;
          }
          return filterCriteria.conditions.some((condition) => isConditionMatching(device, condition));
        })
        .filter((device) => {
          if (!filterCriteria.last30DaysTravel || filterCriteria.last30DaysTravel.length === 0) {
            return true;
          }
          return filterCriteria.last30DaysTravel.some((last30DaysTravel) => {
            switch (last30DaysTravel) {
              case '2000':
                return isLast30DaysMatching(device, 2000, 3000, distUnit);
              case '3000':
                return isLast30DaysMatching(device, 3000, 4000, distUnit);
              case '4000':
                return isLast30DaysMatching(device, 4000, 5000, distUnit);
              case '5000':
                return isLast30DaysMatching(device, 5000, 0, distUnit);
              default:
                return false;
            }
          });
        })
        .filter((device) => {
          if (!filterCriteria.installation || filterCriteria.installation.length === 0) {
            return true;
          }
          return filterCriteria.installation.some((installation) => {
            switch (installation) {
              case 'notinstalled':
                return isInstallationMatching(device, 'notinstalled');
              case 'installed':
                return isInstallationMatching(device, 'installed');
              case '7days':
                return isInstallationMatching(device, '7days');
              case '30days':
                return isInstallationMatching(device, '30days');
              case '60days':
                return isInstallationMatching(device, '60days');
              case '90days':
                return isInstallationMatching(device, '90days');
              default:
                return false;
            }
          });
        });

    return filtered;
  };

  // Helper function to get filtered positions
  const getFilteredPositions = (filteredDevices, allPositions, useFilterMap) => {
    if (!filteredDevices || !allPositions) return [];

    return useFilterMap
      ? filteredDevices.map((device) => allPositions[device.id]).filter(Boolean)
      : Object.values(allPositions);
  };

  // Helper function to perform auto-zoom
  const performAutoZoom = (autoZoomPositions) => {
    setTimeout(() => {
      try {
        if (autoZoomPositions.length > 1) {
          const validPositions = autoZoomPositions.filter((pos) => pos && pos.latitude && pos.longitude);
          if (validPositions.length === 0) return;

          const coordinates = validPositions.map((position) => [position.longitude, position.latitude]);

          const bounds = coordinates.reduce(
            (bounds, coord) => bounds.extend(coord),
            new maplibregl.LngLatBounds(coordinates[0], coordinates[0]),
          );

          const canvas = map.getCanvas();
          if (canvas) {
            map.fitBounds(bounds, {
              duration: 1000,
              padding: Math.min(canvas.width, canvas.height) * 0.1,
            });
          }
        } else if (autoZoomPositions.length === 1) {
          const position = autoZoomPositions[0];
          if (position && position.latitude && position.longitude) {
            map.easeTo({
              center: [position.longitude, position.latitude],
              zoom: Math.max(map.getZoom(), 10),
              duration: 1000,
            });
          }
        }
      } catch (error) {
        // Silently handle errors during auto-zoom to prevent app crashes
        // The auto-zoom is not critical functionality
      }
    }, 100);
  };

  useEffect(() => {
    // Get filtered devices using the helper function
    const filtered = getFilteredDevices(devices, showTerminated, keyword, filter, distanceUnit);

    // Apply sorting
    switch (filterSort) {
      case 'name':
        filtered.sort((device1, device2) => device1.name.localeCompare(device2.name));
        break;
      case 'lastUpdate':
        filtered.sort((device1, device2) => {
          const time1 = device1.lastUpdate ? moment(device1.lastUpdate).valueOf() : 0;
          const time2 = device2.lastUpdate ? moment(device2.lastUpdate).valueOf() : 0;
          return time2 - time1;
        });
        break;
      default:
        break;
    }

    setFilteredDevices(filtered);
  }, [keyword, filter, showTerminated, filterSort, groups, devices, setShowTerminated, setFilteredDevices, distanceUnit]);

  // Separate effect for position updates - ensures positions update in all browsers
  useEffect(() => {
    if (!devices || !positions) return;

    // Get filtered devices using the helper function
    const filtered = getFilteredDevices(devices, showTerminated, keyword, filter, distanceUnit);

    // Get filtered positions using the helper function
    const filteredPositions = getFilteredPositions(filtered, positions, filterMap);

    setFilteredPositions(filteredPositions);
  }, [positions, devices, groups, filter, keyword, showTerminated, filterMap, distanceUnit, setFilteredPositions]);

  // Separate effect for auto zoom - only runs when filter criteria change
  useEffect(() => {
    // Auto zoom when filters change (whether adding or removing filters)
    if (!autoZoomOnFilter || !positions || !map) return;

    // Only run the auto-zoom if we haven't already zoomed for this filter state
    if (hasZoomedRef.current) return;

    // Set the flag to prevent re-zooming until filters change again
    hasZoomedRef.current = true;

    // Get filtered devices using the helper function
    const filtered = getFilteredDevices(devices, showTerminated, keyword, filter, distanceUnit);

    // Get positions for auto-zoom using the helper function
    const autoZoomPositions = getFilteredPositions(filtered, positions, filterMap);

    // Perform auto-zoom using the helper function
    performAutoZoom(autoZoomPositions);
  }, [keyword, filter, showTerminated, filterMap, autoZoomOnFilter, devices, groups, positions]);

  // Reset the zoom flag when filter criteria change so we can auto-zoom again
  useEffect(() => {
    hasZoomedRef.current = false;
  }, [keyword, filter, showTerminated, filterMap]);
};
